import {
  ApiError,
  LicensingAgenciesService,
  MccsRoles,
} from '@/@types/generated'
import { Breadcrumb } from '@/components/Atoms/Breadcrumbs'
import Loading from '@/components/Atoms/Loading'
import { UserStampTooltip } from '@/components/Atoms/UserStampTooltip'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Header } from '@/components/ui/header'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { useError } from '@/hooks/useError'
import format from '@/lib/format'
import { buildPath, Params } from '@/routes'
import { Building2Icon, CheckIcon, PencilIcon } from 'lucide-react'
import { Suspense, useCallback, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router'
import {
  LicensingAgencyProvider,
  useLicensingAgency,
} from '../hooks/licensing-agency-provider'
import LicensingAgencyNavigation from './LicensingAgencyNavigation'
import { LicensingAgencyRejectedAlert } from './LicensingAgencyRejectedAlert'
import { LicensingAgencyStatusBadge } from './LicensingAgencyStatusBadge'
import { LicensingAgencyUnderReviewAlert } from './LicensingAgencyUnderReviewAlert'
import { RevealButton } from './RevealButton'
import { HasFeature, hasRole } from '@/components/Atoms/Permissions'
import { LicensingAgentsFeature } from '@/lib/features'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import {
  BusinessEntityForm,
  TFormSchema,
} from '@/modules/companies/components/business-entity/BusinessEntityForm'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/useAuth'

export function LicensingAgencyLayout() {
  // Hooks
  const { agencyId } = useParams<Params<'/licensing-agencies/:agencyId'>>()
  const navigate = useNavigate()
  const { showError } = useError()

  const handleError = useCallback(
    (error: unknown) => {
      // Show not found error
      if (error instanceof ApiError && error.status === 404) {
        showError('Licensing agency not found')
      }

      // Redirect to licensing agencies index on any error
      navigate(buildPath('/licensing-agencies'))
    },
    [navigate, showError]
  )

  return (
    <LicensingAgencyProvider id={agencyId!} onError={handleError}>
      <Inner />
    </LicensingAgencyProvider>
  )
}

export function Inner() {
  // Hooks
  const { licensingAgency, licensingAgencyUser, reload } = useLicensingAgency()
  const { user } = useAuth()

  // State
  const [isEditing, setIsEditing] = useState(false)

  // Submit handler
  async function handleSubmit(data: TFormSchema) {
    if (!licensingAgency.id) return
    try {
      await LicensingAgenciesService.update(licensingAgency.id, data)
      setIsEditing(false)
      reload(true)
    } catch (_) {
      // No error handling necessary, toasts are handled in the provider
    }
  }

  return (
    <>
      <Breadcrumb
        label={format.CarrierName(licensingAgency)}
        path={buildPath('/licensing-agencies/:agencyId', {
          agencyId: licensingAgency.id,
        })}
      />

      <div className="flex flex-col justify-between gap-4 md:flex-row">
        <div className="flex min-w-0 flex-col gap-2">
          <Header
            variant="h1"
            className="overflow-hidden overflow-ellipsis whitespace-nowrap"
          >
            {format.CarrierName(licensingAgency)}
          </Header>
        </div>
        <div className="flex flex-col items-start gap-1 lg:items-end">
          <div className="my-1 hidden grid-cols-[minmax(0,max-content)_1fr] gap-1 gap-x-2 text-right lg:grid">
            <Label className="justify-self-end text-sm">Created</Label>
            <UserStampTooltip by={licensingAgency.created} />

            <Label className="justify-self-end text-sm">Last Updated</Label>
            <UserStampTooltip by={licensingAgency.modified} />
          </div>
        </div>
      </div>
      {/* Mobile timestamps */}
      <div className="-mt-2 grid grid-cols-2 gap-4 md:flex lg:hidden">
        <div>
          <Label className="text-muted-foreground text-sm lg:text-right">
            Created
          </Label>
          <UserStampTooltip by={licensingAgency.created} />
        </div>

        <div>
          <Label className="text-muted-foreground text-sm lg:text-right">
            Last Updated
          </Label>
          <UserStampTooltip by={licensingAgency.modified} />
        </div>
      </div>

      <Card>
        <CardHeader className="flex items-start justify-between">
          <h3 className="flex items-center gap-2 text-2xl font-semibold lg:col-span-2">
            <Building2Icon className="size-5 flex-none" />
            Agency Information
            <HasFeature feature={LicensingAgentsFeature.Details}>
              {!isEditing && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsEditing(true)}
                    >
                      <PencilIcon className="stroke-primary size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit Agency Information</TooltipContent>
                </Tooltip>
              )}
            </HasFeature>
            <Badge variant="outline" className="h-8 text-sm">
              <LicensingAgencyStatusBadge status={licensingAgency.status} />
            </Badge>
          </h3>
        </CardHeader>

        {/* Display */}
        {!isEditing && (
          <CardContent className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Legal Name</Header>
              <Header variant="h5">{licensingAgency.legalName}</Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">
                Doing Business As (DBA) Name
              </Header>
              <Header variant="h5">
                {licensingAgency.dbaName || '\u2014'}
              </Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">
                Physical Address
              </Header>
              <Header variant="h5">
                {format.Address(licensingAgency.physicalAddress)}
              </Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Mailing Address</Header>
              <Header variant="h5">
                {format.Address(licensingAgency.mailingAddress)}
              </Header>
            </div>

            <Separator className="my-2 sm:col-span-2" />

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Tax ID</Header>
              <Header variant="h5" className="flex items-center gap-2">
                <RevealButton
                  getSecret={
                    () =>
                      LicensingAgenciesService.getTaxId(
                        licensingAgency.id
                      ).finally(() => reload(true)) // reload licensing agency history (in the background)
                  }
                  canReveal={
                    licensingAgencyUser?.role ===
                      MccsRoles.LICENSING_AGENCY_OWNER ||
                    hasRole(
                      [MccsRoles.CREDENTIALING_ADMIN, MccsRoles.CREDENTIALING],
                      user
                    )
                  }
                >
                  {licensingAgency.taxId}
                </RevealButton>
              </Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Owner Name</Header>
              <Header variant="h5">{licensingAgency.ownerName}</Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Phone Number</Header>
              <Header variant="h5">
                {format.PhoneNumber(licensingAgency.phoneNumber)}
              </Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Email Address</Header>
              <Header variant="h5">{licensingAgency.emailAddress}</Header>
            </div>
          </CardContent>
        )}

        {/* Edit mode */}
        {isEditing && (
          <CardContent className="grid grid-cols-1 gap-x-8 gap-y-2">
            <div className="flex flex-col gap-4">
              <BusinessEntityForm
                initialValues={{
                  ...licensingAgency,
                  ownerInfo: {
                    name: licensingAgency.ownerName ?? '',
                    phoneNumber: licensingAgency.phoneNumber ?? '',
                    email: licensingAgency.emailAddress ?? '',
                  },
                  physicalAddress: {
                    ...licensingAgency.physicalAddress,
                    street1: licensingAgency.physicalAddress.street1 ?? '',
                    street2: licensingAgency.physicalAddress.street2 ?? '',
                    city: licensingAgency.physicalAddress.city ?? '',
                    stateProvince:
                      licensingAgency.physicalAddress.stateProvince ?? '',
                    postalCode:
                      licensingAgency.physicalAddress.postalCode ?? '',
                  },
                  mailingAddress: {
                    ...licensingAgency.mailingAddress,
                    street1: licensingAgency.mailingAddress?.street1 ?? '',
                    street2: licensingAgency.mailingAddress?.street2 ?? '',
                    city: licensingAgency.mailingAddress?.city ?? '',
                    stateProvince:
                      licensingAgency.mailingAddress?.stateProvince ?? '',
                    postalCode:
                      licensingAgency.mailingAddress?.postalCode ?? '',
                  },
                }}
                onSubmit={handleSubmit}
                withTaxId
                withOwnerInfo
              >
                {({ isSubmitting }) => (
                  <div className="flex gap-2 lg:col-span-2">
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? <Spinner /> : <CheckIcon />}
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </BusinessEntityForm>
            </div>
          </CardContent>
        )}
      </Card>

      <LicensingAgencyUnderReviewAlert />

      <LicensingAgencyRejectedAlert />

      <LicensingAgencyNavigation licensingAgency={licensingAgency} />

      <Suspense fallback={<Loading variant="fullscreen" />}>
        <Outlet />
      </Suspense>
    </>
  )
}
